// src/components/gestionEvento/CompetitionTabs.js
import React, { useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  AppBar, Tabs, Tab, Box, Toolbar, Typography, Alert, Paper
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Sports as SportsIcon,
  Timer as TimerIcon,
  EventNote as EventNoteIcon,
  Schedule as ScheduleIcon,
  FormatListNumbered as FormatListNumberedIcon,
  SwapVert as SwapVertIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { styled, alpha } from '@mui/material/styles';

import { ReactComponent as IntegrationIcon } from '../../assets/icons/Integration.svg';

import { useCompetencia }   from '../../context/CompetenciaContext';
import AjustesForm          from './Ajustes/AjustesForm';
import ConvocatoriaForm     from './Ajustes/ConvocatoriaForm';
import PruebasForm          from './Ajustes/PruebasForm';
import DashboardInscritos   from './Ajustes/Competidores/DashboardInscritos';
import TeamsTable           from './TeamsTable';
import SessionManager       from './SessionManager';
import SiembraManager       from './SiembraManager';
import CompetitionPreview   from './CompetitionPreview';
import EventList            from '../event/EventList';
import IntegracionesManager from './IntegracionesManager';
import SigertreLoader       from '../../components/Common/SigertreLoader';
import { Typography as MuiTypography } from '@mui/material';

/* ── Helper para “romper” el contenedor padre y usar 100vw ── */
const Bleed = ({ children, sx }) => (
  <Box
    sx={{
      position: 'relative',
      left: '50%',
      right: '50%',
      ml: '-50vw',
      mr: '-50vw',
      width: '100vw',
      ...sx
    }}
  >
    {children}
  </Box>
);

/* ── Estilos ── */
const HeaderBar = styled(AppBar)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  backdropFilter: 'blur(8px)',
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  // línea más tenue
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.22)}`,
  boxShadow: 'none',
  zIndex: theme.zIndex.appBar + 1
}));

const TabsWrapper = styled(Paper)(({ theme }) => ({
  width: '100%',
  borderRadius: 16,
  padding: 6,
  boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.06)}`,
  background: alpha(theme.palette.background.paper, 0.9),
  // borde más suave
  border: `1px solid ${alpha(theme.palette.divider, 0.16)}`
}));

const PillTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 44,
  '& .MuiTabs-indicator': { display: 'none' },
  '& .MuiTabs-scrollButtons': { color: theme.palette.text.disabled }
}));

const PillTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  minHeight: 42,
  height: 42,
  borderRadius: 999,
  paddingInline: 12,
  marginInline: 4,
  fontWeight: 600,
  letterSpacing: 0.1,
  transition: 'all .2s ease',
  color: theme.palette.text.secondary,
  '& .MuiTab-iconWrapper': { marginRight: 8 },
  '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.06) },
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.22)}`
  },
}));

const ContentCard = styled(Paper)(({ theme }) => ({
  width: '100%',
  borderRadius: 16,
  boxShadow: `0 12px 36px ${alpha(theme.palette.common.black, 0.05)}`,
  // borde más tenue
  border: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
  background: theme.palette.background.paper
}));

/* ─────────────────────────────────────── */

const CompetitionTabs = () => {
  const { competitionId } = useParams();
  const navigate          = useNavigate();
  const { pathname }      = useLocation();

  const {
    competencias,
    setCompetencia,
    updateConvocatoria,
    deleteCompetencia: deleteCompetenciaCtx,
    loading: loadingCompetencias
  } = useCompetencia();

  const competencia = !loadingCompetencias
    ? competencias.find(c => c.id === competitionId)
    : null;

  const tabs = useMemo(() => ([
    { label:'Ajustes',        icon:<SettingsIcon fontSize="small" />,            component:AjustesForm,        tabKey:'ajustes' },
    { label:'Convocatoria',   icon:<AssignmentIcon fontSize="small" />,          component:ConvocatoriaForm,   tabKey:'convocatoria' },
    { label:'Pruebas',        icon:<EventNoteIcon fontSize="small" />,           component:PruebasForm,        tabKey:'pruebas' },
    { label:'Competidores',   icon:<SportsIcon fontSize="small" />,              component:DashboardInscritos, tabKey:'competidores' },
    { label:'Equipos',        icon:<PersonIcon fontSize="small" />,              component:TeamsTable,         tabKey:'equipos' },
    { label:'Horarios',       icon:<ScheduleIcon fontSize="small" />,            component:SessionManager,     tabKey:'horarios' },
    { label:'Siembra',        icon:<SwapVertIcon fontSize="small" />,            component:SiembraManager,     tabKey:'siembra' },
    { label:'Jueces',         icon:<TimerIcon fontSize="small" />,               component:()=><MuiTypography>Contenido de Jueces</MuiTypography>,          tabKey:'jueces' },
    { label:'Numeración',     icon:<FormatListNumberedIcon fontSize="small" />,  component:()=><MuiTypography>Contenido de Numeración</MuiTypography>,      tabKey:'numeracion' },
    { label:'Records',        icon:<RecordVoiceOverIcon fontSize="small" />,     component:EventList,          tabKey:'records' },
    { label:'Integraciones',  icon:<IntegrationIcon style={{ width: 18, height: 18 }} />, component:IntegracionesManager, tabKey:'integraciones' },
    { label:'Notificaciones', icon:<NotificationsIcon fontSize="small" />,       component:()=><MuiTypography>Contenido de Notificaciones</MuiTypography>,  tabKey:'notificaciones' },
    { label:'Vista Previa',   icon:<OpenInNewIcon fontSize="small" />,           component:CompetitionPreview, tabKey:'vistaprevia' }
  ]), []);

  const basePath  = `/${competitionId}/`;
  const segment   = pathname.startsWith(basePath)
    ? pathname.replace(basePath,'').split('/')[0]
    : '';
  const activeKey = segment || 'ajustes';
  const activeIdx = Math.max(0, tabs.findIndex(t => t.tabKey === activeKey));

  if (loadingCompetencias) {
    return (
      <Box sx={{display:'flex',justifyContent:'center',p:4}}>
        <SigertreLoader message="Cargando competencias…" />
      </Box>
    );
  }

  if (!competencia) {
    return (
      <Bleed>
        <Box sx={{py:3, px: { xs: 2, md: 3 } }}>
          <Alert severity="error">
            No se encontró la competencia con ID: <strong>{competitionId}</strong>
          </Alert>
        </Box>
      </Bleed>
    );
  }

  const handleTabChange = (_evt, newIdx) => {
    const { tabKey } = tabs[newIdx];
    navigate(`/${competitionId}/${tabKey}`);
  };

  const { component: ActiveComponent, tabKey } = tabs[activeIdx];
  const isFullBleedSection = ['pruebas', 'ajustes', 'convocatoria', 'competidores'].includes(tabKey);

  let extraProps = {};
  switch (tabKey) {
    case 'ajustes':
      extraProps = { competencia, setCompetencia, onDelete: deleteCompetenciaCtx };
      break;
    case 'convocatoria':
      extraProps = { competencia, updateConvocatoria };
      break;
    case 'integraciones':
      extraProps = { competitionId };
      break;
    default:
      if (!['jueces','numeracion','notificaciones'].includes(tabKey)) {
        extraProps = { competencia };
      }
  }

  return (
    <Box sx={{ flexGrow: 1, width: '100%' }}>
      {/* ─── Header 100vw ─── */}
      <Bleed>
        <HeaderBar elevation={0} color="transparent">
          <Toolbar sx={{ minHeight: 64, px: { xs: 2, md: 3 } }}>
            <Typography
              variant="h6"
              sx={{
                flexGrow: 1,
                fontWeight: 800,
                letterSpacing: 0.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {competencia.general?.nombre || 'Gestión de competencia'}
            </Typography>
            {/* Select removido para un header más limpio */}
          </Toolbar>
        </HeaderBar>
      </Bleed>

      {/* ─── Tabs 100vw ─── */}
<Bleed sx={{ mt: 2 }}>
  <Box sx={{ px: { xs: 2, md: 3 } }}>
    <PillTabs
      value={activeIdx}
      onChange={handleTabChange}
      variant="scrollable"
      scrollButtons="auto"
      aria-label="Navegación de secciones de la competencia"
      sx={{
        // sin contenedor: ocupa su ancho, misma respiración
        width: '100%',
        px: 0.5,
        backgroundColor: 'transparent',
      }}
    >
      {tabs.map(t => (
        <PillTab
          key={t.tabKey}
          icon={t.icon}
          iconPosition="start"
          label={t.label}
          aria-controls={`tabpanel-${t.tabKey}`}
          id={`tab-${t.tabKey}`}
        />
      ))}
    </PillTabs>

    {/* hairline divider muy tenue */}
    <Box
      sx={(theme) => ({
        height: 1,
        mt: 0.5,
        backgroundColor: alpha(theme.palette.divider, 0.14),
      })}
    />
  </Box>
</Bleed>

      {/* ─── Contenido 100vw ─── */}
      <Bleed sx={{ mt: 2 }}>
        <Box sx={{ px: { xs: 2, md: 3 } }}>
          {isFullBleedSection ? (
            <Box>
              <ActiveComponent key={`${competitionId}-${tabKey}`} {...extraProps} />
            </Box>
          ) : (
            <ContentCard sx={{ p: { xs: 2, md: 3 } }}>
              <ActiveComponent key={`${competitionId}-${tabKey}`} {...extraProps} />
            </ContentCard>
          )}
        </Box>
      </Bleed>
    </Box>
  );
};

export default CompetitionTabs;
